import { useState } from 'react';
import useAppContext from '../../../hooks/useAppContext';
import { AlertType } from '../../../design-system/DSAlert';
import useClockDataListener from '../../Klydos/hooks/useClockDataListener';
import useAddClockToUserMutation from '../../Klydos/hooks/useAddClockToUserMutation';
import useRemoveClockFromUserMutation from '../../Klydos/hooks/useRemoveClockFromUserMutation';

const useMyClock = () => {
  const { clock, refreshToken, showAlert } = useAppContext();
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);

  const { data: clockData, isLoading: isLoadingClockData } =
    useClockDataListener(clock);

  const { mutate: addClockMutation, isPending: isConnecting } =
    useAddClockToUserMutation({
      suppressErrorAlert: true,
      onSuccess: async () => {
        // The connect already succeeded server-side; a failed token refresh
        // just delays the claim update to the next load, so don't let it
        // reject the success path.
        await refreshToken().catch(() => undefined);
        setIsConnectModalOpen(false);
        showAlert({
          message: 'Your device has been connected',
          alertType: AlertType.SUCCESS,
        });
      },
      onError: () => {
        showAlert({
          message: 'Failed to connect your device',
          alertType: AlertType.ERROR,
        });
      },
    });

  const { mutate: removeClockMutation, isPending: isDisconnecting } =
    useRemoveClockFromUserMutation({
      suppressErrorAlert: true,
      onSuccess: async () => {
        await refreshToken().catch(() => undefined);
        setIsDisconnectDialogOpen(false);
        showAlert({
          message: 'Your device has been disconnected',
          alertType: AlertType.SUCCESS,
        });
      },
      onError: () => {
        showAlert({
          message: 'Failed to disconnect your device',
          alertType: AlertType.ERROR,
        });
      },
    });

  const handleConnect = (serialNumber: string) => {
    addClockMutation({ clockId: serialNumber });
  };

  const handleDisconnect = () => {
    removeClockMutation();
  };

  return {
    isConnected: !!clock,
    clockName: clockData?.idf,
    isLoadingClockData,
    isConnectModalOpen,
    openConnectModal: () => setIsConnectModalOpen(true),
    closeConnectModal: () => setIsConnectModalOpen(false),
    isDisconnectDialogOpen,
    openDisconnectDialog: () => setIsDisconnectDialogOpen(true),
    closeDisconnectDialog: () => setIsDisconnectDialogOpen(false),
    handleConnect,
    handleDisconnect,
    isConnecting,
    isDisconnecting,
  };
};

export default useMyClock;
